import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

import { utcToZonedTime } from 'date-fns-tz'
import { get, isNil, pickBy } from 'lodash-es'

import {
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
  COMMON_SEARCH_FOR_CARE_SCREENS,
  COUNTRY_CATEGORY,
  GCT_TREATMENT_OPTION_DETAIL,
  getISO8601DateWithTimezone,
  getShouldShowUrgencyToBookv2,
  LATEST_PROVIDER_PROPERTIES,
  makeProviderKeywordTrackingData,
  makeProvidersListKeywordTrackingData,
  MIXPANEL_PROPERTY,
  PARENTAL_LEAVE_TREATMENT_OPTION_DETAIL,
  S4C_PROPERTIES,
  SEARCHER_TYPE_TO_MATCH_FOR,
  TreatmentRecommendations,
  URGENCY_TO_BOOK_PROPERTIES,
  useFlags,
  WELLNESS_TOOLKIT_EVENTS_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic'
import {
  parentalLeaveUtmCampaignString,
  STICKY_QUERY_PARAMS,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/utm'
import { ChildInfo } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/profileSelectionForm/ProfileSelectionForm'

import { useDefaultMixpanelEntryPoint } from './useDefaultMixpanelEntryPoint'
import { getCustomerConfigurationValue } from '../../data/customer/customerSelectors'
import { getTriageEntryPoint } from '../../data/register/registerSelectors'
import {
  getIsUserInternational,
  getTimeZoneOrDefault,
  getTimeZoneOrDefaultIgnoreOnboarding,
  getUserAge,
  getUserChildren,
  getUserCountryName,
  getUserModalityPreference,
} from '../../data/user/userSelectors'
import {
  getIsLatestMatches,
  getIsOnboardProviderFavoritedProvider,
  getLatestMatchesSearchDate,
  getLatestUserFavoriteProviders,
  getOnboardAppointmentDate,
  getOnboardAppointmentTime,
  getOnboardDirectLinkIntent,
  getOnboardDirectLinkSource,
  getOnboardIsCurrentSearchDirectPath,
  getOnboardProviderCapacity,
  getOnboardProviderLyraId,
  getOnboardProviderProfileData,
  getOnboardProvidersDisplayed,
  getOnboardProviderTaxonomyData,
  getOnboardProviderTotalSessionCost,
  getOnboardSelectedHealthPlanName,
  getOnboardSelectedTreatmentOption,
  getOnboardSelectedTreatmentOptionCTPO,
  getOnboardToolkitType,
  getOnboardUserFavoriteProviderRequestId,
} from '../../features/onboard/data/onboardSelectors'
import { getTriageDemographicMatchFor } from '../../features/onboard/data/triage-demographics/triageDemographicsSelectors'
import {
  getSearcherAge,
  getSelectedSearch,
  getSelectedSearchId,
  getShownTierPage,
  getTreatmentRecommendations,
} from '../../features/triage/data/triageSelectors'

const SCREENS_WITH_SHARED_PROPS = [
  COMMON_MIXPANEL_PAGE.PROVIDER_SCHEDULING_CALENDAR,
  COMMON_MIXPANEL_PAGE.APPOINTMENT_BOOKING,
  COMMON_MIXPANEL_PAGE.APPOINTMENT_REQUEST,
  COMMON_MIXPANEL_PAGE.PROVIDER_RESULTS,
  COMMON_MIXPANEL_PAGE.LOCATION,
  COMMON_MIXPANEL_PAGE.ZERO_RESULTS,
  COMMON_SEARCH_FOR_CARE_SCREENS.SEARCH_FOR_CARE_LOADING,
]

// Careful when calling makeProviderDataForTracking as it can be very expensive - be wary of putting it inside a function that calls useGetSearchForCareEventProps often
export const useGetSearchForCareEventProps = () => {
  const { renderSaveYourSpotV2Assignment, gctProviders, showProviderAdjectives } = useFlags()
  const [searchParams] = useSearchParams()
  const utmCampaign = searchParams.get(STICKY_QUERY_PARAMS.UTM_CAMPAIGN)
  const isParentalLeaveCoaching = utmCampaign?.includes(parentalLeaveUtmCampaignString)

  const defaultMixpanelEntryPoint = useDefaultMixpanelEntryPoint()
  const healthPlan = useSelector(getOnboardSelectedHealthPlanName)
  const memberTimezone = useSelector(getTimeZoneOrDefault)
  const matcherRequestId = useSelector(getOnboardUserFavoriteProviderRequestId)
  const selectedTreatmentOption = useSelector(getOnboardSelectedTreatmentOption)
  const selectedTreatmentOptionCPTO = useSelector(getOnboardSelectedTreatmentOptionCTPO)
  const treatmentRecommendationsImmutable = useSelector(getTreatmentRecommendations)
  const treatmentRecommendations = useMemo(
    () => treatmentRecommendationsImmutable?.toJS(),
    [treatmentRecommendationsImmutable],
  ) as TreatmentRecommendations
  const eligibleTreatmentOptions = treatmentRecommendations?.eligibleTreatmentOptions
  const selectedSearchImmutable = useSelector(getSelectedSearch)
  const selectedSearch = useMemo(() => selectedSearchImmutable?.toJS(), [selectedSearchImmutable])
  const selectedChildLyraId = selectedSearch?.body?.child_lyra_id
  const meetingFormat = useSelector(getUserModalityPreference)
  const customerConfigurationValue = useSelector(getCustomerConfigurationValue)
  const isMemberInternational = useSelector(getIsUserInternational)
  const isLatestMatches = useSelector(getIsLatestMatches)
  const latestUserProviders = useSelector(getLatestUserFavoriteProviders)
  const matchFor = useSelector(getTriageDemographicMatchFor)
  const lastSearchTime = useSelector(getLatestMatchesSearchDate)
  const childrenImmutable = useSelector(getUserChildren)
  const children = useMemo(() => childrenImmutable?.toJS(), [childrenImmutable])
  const registeredCountry = useSelector(getUserCountryName)
  const searchId = useSelector(getSelectedSearchId)
  const selectedProviderId = useSelector(getOnboardProviderLyraId)
  const isProviderFavorited = useSelector(getIsOnboardProviderFavoritedProvider)
  const totalSessionCost = useSelector(getOnboardProviderTotalSessionCost)
  const appointmentDate = useSelector(getOnboardAppointmentDate)
  const appointmentTime = useSelector(getOnboardAppointmentTime)
  const timeZone = useSelector(getTimeZoneOrDefaultIgnoreOnboarding)
  const latestUserProvidersLastRequestId = latestUserProviders?.[latestUserProviders.length - 1]?.requestId
  const isCurrentSearchDirectPath = useSelector(getOnboardIsCurrentSearchDirectPath)
  const directLinkIntent = useSelector(getOnboardDirectLinkIntent)
  const directLinkSource = useSelector(getOnboardDirectLinkSource)
  const clientTierLevel = useSelector(getShownTierPage)
  const searcherAge = useSelector(getSearcherAge)
  const userAge = useSelector(getUserAge)
  const guardianAge = matchFor === SEARCHER_TYPE_TO_MATCH_FOR.OTHER_CHILD ? userAge : undefined
  const triageEntryPoint = useSelector(getTriageEntryPoint)
  const selectedProviderProgramTaxonomyImmutable = useSelector(getOnboardProviderTaxonomyData)
  const selectedProviderProgramTaxonomy = useMemo(
    () => selectedProviderProgramTaxonomyImmutable?.toJS(),
    [selectedProviderProgramTaxonomyImmutable],
  )
  const providerCapacityImmutable = useSelector(getOnboardProviderCapacity)
  const providerCapacity = useMemo(() => providerCapacityImmutable?.toJS(), [providerCapacityImmutable])
  const providers = useSelector(getOnboardProvidersDisplayed)

  const selectedProviderImmutable = useSelector(getOnboardProviderProfileData)
  const selectedProvider = useMemo(() => selectedProviderImmutable?.toJS(), [selectedProviderImmutable])

  const urgencyToBookFlagVal =
    selectedProviderId != undefined &&
    getShouldShowUrgencyToBookv2({
      selectedTreatmentOption,
      programTaxonomy: selectedProviderProgramTaxonomy,
      providerCapacity,
    })
      ? get(renderSaveYourSpotV2Assignment, '[0]', undefined)
      : 'undefined'

  const toolkitPromotion = useSelector(getOnboardToolkitType)

  return useCallback(
    (
      customProps?: { [key in MIXPANEL_PROPERTY]?: any },
      screen?: COMMON_MIXPANEL_PAGE | COMMON_SEARCH_FOR_CARE_SCREENS | string,
      outsideOnboarding?: boolean,
    ) => {
      const extractTier = (tier?: string) => tier?.replace(/\D+/, '')
      let treatmentOptionDetail =
        outsideOnboarding && Array.isArray(gctProviders) && gctProviders.includes(selectedProviderId)
          ? GCT_TREATMENT_OPTION_DETAIL
          : undefined
      if (isParentalLeaveCoaching) {
        treatmentOptionDetail = PARENTAL_LEAVE_TREATMENT_OPTION_DETAIL
      }

      const props: { [key in MIXPANEL_PROPERTY]?: any } = {
        [COMMON_MIXPANEL_PROPERTIES.CUSTOMER_CONFIGURATION]: customerConfigurationValue,
        [COMMON_MIXPANEL_PROPERTIES.USER_TIMEZONE]: memberTimezone,
        [COMMON_MIXPANEL_PROPERTIES.COUNTRY_CATEGORY]: isMemberInternational
          ? COUNTRY_CATEGORY.INTERNATIONAL
          : COUNTRY_CATEGORY.DOMESTIC,
        [COMMON_MIXPANEL_PROPERTIES.REGISTERED_COUNTRY]: registeredCountry,
        [S4C_PROPERTIES.HEALTH_PLAN_NAME]: healthPlan,
        [S4C_PROPERTIES.CHILD_LYRA_ID]: selectedChildLyraId,
        [S4C_PROPERTIES.MATCHER_REQUEST_ID]: isLatestMatches ? latestUserProvidersLastRequestId : matcherRequestId,
        [S4C_PROPERTIES.TREATMENT_OPTION]: selectedTreatmentOption,
        [S4C_PROPERTIES.MEETING_FORMAT]: meetingFormat,
        [S4C_PROPERTIES.SEARCH_ID]: searchId,
        [S4C_PROPERTIES.MATCH_FOR]: matchFor,
        [S4C_PROPERTIES.AGE]: searcherAge,
        [S4C_PROPERTIES.GUARDIAN_AGE]: guardianAge,
        [LATEST_PROVIDER_PROPERTIES.LAST_SEARCH_TIME]: lastSearchTime,
        [LATEST_PROVIDER_PROPERTIES.PROVIDER_AVAILABILITY]: null, // this property is set at the time the provider results component is rendered
        [LATEST_PROVIDER_PROPERTIES.PAST_PROVIDER_RESULTS_FLAG]: isLatestMatches,
        page: screen,
        ...(outsideOnboarding && { [S4C_PROPERTIES.IS_DIRECT_BOOKING]: true }),
        ...(treatmentOptionDetail && { [S4C_PROPERTIES.TREATMENT_OPTION_DETAIL]: treatmentOptionDetail }),
        ...(SCREENS_WITH_SHARED_PROPS.some((screenWithSharedProps) => screenWithSharedProps === screen) && {
          [S4C_PROPERTIES.IS_DIRECT_PATH]: isCurrentSearchDirectPath,
          [S4C_PROPERTIES.TREATMENT_OPTION_CTPO]: selectedTreatmentOptionCPTO,
          [S4C_PROPERTIES.DIRECT_LINK_INTENT]: directLinkIntent,
          [S4C_PROPERTIES.DIRECT_LINK_SOURCE]: directLinkSource,
        }),
      }

      switch (screen) {
        case COMMON_SEARCH_FOR_CARE_SCREENS.CHILD_SELECTION:
          props[S4C_PROPERTIES.CHILD_LYRA_IDS] = children?.map((child: ChildInfo) => child.lyraId)
          break
        case COMMON_MIXPANEL_PAGE.PROVIDER_PROFILE:
          props[S4C_PROPERTIES.PROVIDER_ID] = selectedProviderId
          props[S4C_PROPERTIES.IS_PROVIDER_SAVED_IN_FAVORITES] = isProviderFavorited
          if (showProviderAdjectives && selectedProvider) {
            const providerKeywordData = makeProviderKeywordTrackingData({
              showProviderAdjectives,
              provider: selectedProvider,
              includeSharedData: false,
            })
            props[S4C_PROPERTIES.TESTIMONIAL_ADJECTIVES] = providerKeywordData.testimonial_adjectives
            props[S4C_PROPERTIES.BIO_KEYWORDS] = providerKeywordData.bio_keywords
          }

          break
        case COMMON_MIXPANEL_PAGE.PROVIDER_SCHEDULING_CALENDAR:
          props[S4C_PROPERTIES.PROVIDER_ID] = selectedProviderId
          props[S4C_PROPERTIES.IS_PROVIDER_SAVED_IN_FAVORITES] = isProviderFavorited
          props[URGENCY_TO_BOOK_PROPERTIES.URGENCY_TO_BOOK_FLAG] = urgencyToBookFlagVal
          props[URGENCY_TO_BOOK_PROPERTIES.PROVIDER_CAPACITY] = providerCapacity
          props[COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT] = defaultMixpanelEntryPoint?.entryPoint
          if (toolkitPromotion) {
            props[WELLNESS_TOOLKIT_EVENTS_PROPERTIES.TOOLKIT_TOPIC] = toolkitPromotion
          }
          break
        case COMMON_MIXPANEL_PAGE.APPOINTMENT_BOOKING:
          props[S4C_PROPERTIES.PROVIDER_ID] = selectedProviderId
          props[S4C_PROPERTIES.SESSION_COST] = totalSessionCost
          props[URGENCY_TO_BOOK_PROPERTIES.URGENCY_TO_BOOK_FLAG] = urgencyToBookFlagVal
          props[URGENCY_TO_BOOK_PROPERTIES.PROVIDER_CAPACITY] = providerCapacity
          props[COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT] = defaultMixpanelEntryPoint?.entryPoint
          props[S4C_PROPERTIES.IS_PROVIDER_SAVED_IN_FAVORITES] = isProviderFavorited
          if (appointmentDate && appointmentTime) {
            const date = utcToZonedTime(`${appointmentDate}T${appointmentTime}`, timeZone)
            const calendarSlotClicked = getISO8601DateWithTimezone({
              date,
              timeZone,
            })
            props[S4C_PROPERTIES.CALENDAR_SLOT_CLICKED] = calendarSlotClicked
          }
          if (toolkitPromotion) {
            props[WELLNESS_TOOLKIT_EVENTS_PROPERTIES.TOOLKIT_TOPIC] = toolkitPromotion
          }
          break
        case COMMON_MIXPANEL_PAGE.TREATMENT_RECOMMENDATIONS:
          props[S4C_PROPERTIES.CLINICAL_TIER] = extractTier(treatmentRecommendations?.modelRecommendation)
          props[S4C_PROPERTIES.CLINICAL_TIER_WITH_EXCLUSIONS] = extractTier(
            treatmentRecommendations?.clinicalRecommendation,
          )
          props[S4C_PROPERTIES.PAGE_LAYOUT] = treatmentRecommendations?.shownTierPage
          props[S4C_PROPERTIES.RECOMMENDED_PROGRAMS] = eligibleTreatmentOptions
          props[S4C_PROPERTIES.PRIMARY_RECOMMENDATIONS] = treatmentRecommendations?.primaryTreatmentOptions ?? []
          props[S4C_PROPERTIES.SECONDARY_RECOMMENDATIONS] = treatmentRecommendations?.secondaryTreatmentOptions ?? []
          break
        case COMMON_MIXPANEL_PAGE.APPOINTMENT_REQUEST:
          props[S4C_PROPERTIES.PROVIDER_ID] = selectedProviderId
          props[S4C_PROPERTIES.IS_PROVIDER_SAVED_IN_FAVORITES] = isProviderFavorited
          if (toolkitPromotion) {
            props[WELLNESS_TOOLKIT_EVENTS_PROPERTIES.TOOLKIT_TOPIC] = toolkitPromotion
          }
          break
        case COMMON_MIXPANEL_PAGE.PROVIDER_RESULTS:
          if (toolkitPromotion) {
            props[WELLNESS_TOOLKIT_EVENTS_PROPERTIES.TOOLKIT_TOPIC] = toolkitPromotion
          }

          if (showProviderAdjectives) {
            props[S4C_PROPERTIES.ADJECTIVES_AND_KEYWORDS_SHOWN] = makeProvidersListKeywordTrackingData({
              providers,
              showProviderAdjectives,
            })
          }
          break

        case COMMON_MIXPANEL_PAGE.LOCATION:
        case COMMON_MIXPANEL_PAGE.ZERO_RESULTS:
        case COMMON_SEARCH_FOR_CARE_SCREENS.SEARCH_FOR_CARE_LOADING:
          if (toolkitPromotion) {
            props[WELLNESS_TOOLKIT_EVENTS_PROPERTIES.TOOLKIT_TOPIC] = toolkitPromotion
          }
          break
        case COMMON_MIXPANEL_PAGE.RECOMMENDATIONS_EXPLAINED_PAGE:
        case COMMON_MIXPANEL_PAGE.PREFERENCES_CATEGORY_PAGE:
          props[S4C_PROPERTIES.TREATMENT_OPTION] = selectedTreatmentOption
          props[S4C_PROPERTIES.SEARCH_ID] = searchId
          props[S4C_PROPERTIES.IS_DIRECT_PATH] = isCurrentSearchDirectPath
          props[S4C_PROPERTIES.CLIENT_TIER_LEVEL] = clientTierLevel
          break
        case COMMON_SEARCH_FOR_CARE_SCREENS.TRIAGE_SUMMARY:
          props[COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT] = triageEntryPoint
          props[COMMON_MIXPANEL_PROPERTIES.ARM] = 'experiment'
      }

      const propsWithNonEmptyValues = pickBy(props, (value) => !isNil(value))
      return { ...propsWithNonEmptyValues, ...customProps }
    },
    [
      gctProviders,
      selectedProviderId,
      isParentalLeaveCoaching,
      customerConfigurationValue,
      memberTimezone,
      isMemberInternational,
      registeredCountry,
      healthPlan,
      selectedChildLyraId,
      isLatestMatches,
      latestUserProvidersLastRequestId,
      matcherRequestId,
      selectedTreatmentOption,
      meetingFormat,
      searchId,
      matchFor,
      searcherAge,
      guardianAge,
      lastSearchTime,
      children,
      isProviderFavorited,
      showProviderAdjectives,
      isCurrentSearchDirectPath,
      selectedTreatmentOptionCPTO,
      directLinkIntent,
      directLinkSource,
      urgencyToBookFlagVal,
      providerCapacity,
      defaultMixpanelEntryPoint?.entryPoint,
      toolkitPromotion,
      totalSessionCost,
      appointmentDate,
      appointmentTime,
      treatmentRecommendations?.modelRecommendation,
      treatmentRecommendations?.clinicalRecommendation,
      treatmentRecommendations?.shownTierPage,
      treatmentRecommendations?.primaryTreatmentOptions,
      treatmentRecommendations?.secondaryTreatmentOptions,
      eligibleTreatmentOptions,
      clientTierLevel,
      triageEntryPoint,
      selectedProvider,
      timeZone,
      providers,
    ],
  )
}
